import React, { useEffect, useState, useRef } from 'react';

/**
 * FuelPriceDeltaToast — Phase 3+ Enhancement (audit doc 11.06.2026)
 *
 * Listens for the global `fuel-price-delta` CustomEvent dispatched by the
 * cache module after a successful background refresh of /api/fuel/prices/live.
 * Renders a small, auto-dismissing toast showing which fuel types moved.
 *
 * Design choices:
 *   • Bottom-centred on mobile, bottom-right on ≥sm screens.
 *   • Auto-dismisses after 4 s; user can tap × to dismiss early.
 *   • Debounce: ignore further events for 30 s after showing one, to avoid
 *     stacking when multiple endpoints refresh in quick succession.
 *   • Only renders deltas for the AVERAGES endpoint (cacheKey ends in
 *     `/api/fuel/prices/live` or starts with 'averages'). Station-level
 *     micro-changes would be visual noise.
 */
const DEBOUNCE_MS = 30 * 1000;
const VISIBLE_MS = 4 * 1000;

const FuelPriceDeltaToast = () => {
  const [event, setEvent] = useState(null); // { items: [...] } | null
  const lastShownRef = useRef(0);

  useEffect(() => {
    const handler = (e) => {
      const detail = e?.detail;
      if (!detail || !detail.deltas?.items?.length) return;

      // Only fire on AVERAGES cache keys — station-level deltas are noisy.
      const key = (detail.cacheKey || '').toLowerCase();
      const isAveragesKey =
        key.includes('averages') ||
        key.includes('/api/fuel/prices/live');
      if (!isAveragesKey) return;

      // Debounce — at most one toast per 30s.
      if (Date.now() - lastShownRef.current < DEBOUNCE_MS) return;
      lastShownRef.current = Date.now();

      setEvent(detail.deltas);
      const t = setTimeout(() => setEvent(null), VISIBLE_MS);
      // store cleanup on the ref so a second event clears the prior timer
      handler._t = t;
    };

    window.addEventListener('fuel-price-delta', handler);
    return () => {
      window.removeEventListener('fuel-price-delta', handler);
      if (handler._t) clearTimeout(handler._t);
    };
  }, []);

  if (!event) return null;

  return (
    <div
      className="fixed z-[60] bottom-20 left-1/2 -translate-x-1/2 sm:left-auto sm:right-6 sm:translate-x-0 max-w-[92vw]"
      role="status"
      aria-live="polite"
      data-testid="fuel-price-delta-toast"
    >
      <div className="flex items-center gap-3 bg-white border border-gray-200 shadow-lg rounded-full pl-4 pr-2 py-2 animate-in fade-in slide-in-from-bottom-2 duration-300">
        <span className="text-base" aria-hidden="true">⛽</span>
        <div className="text-sm font-medium text-gray-800 flex items-center gap-3 flex-wrap">
          <span className="text-gray-500">Prices just updated ·</span>
          {event.items.slice(0, 3).map((item) => {
            const isDown = item.delta < 0;
            return (
              <span
                key={item.fuelType}
                className={`inline-flex items-center gap-1 ${isDown ? 'text-green-600' : 'text-amber-600'}`}
                data-testid={`fuel-delta-${item.fuelType}`}
              >
                <span className="font-semibold">{item.label}</span>
                <span className="font-bold">{item.symbol}</span>
                <span className="tabular-nums">{Math.abs(item.delta).toFixed(1)}p</span>
              </span>
            );
          })}
        </div>
        <button
          onClick={() => setEvent(null)}
          className="ml-1 w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
          aria-label="Dismiss price update"
          data-testid="fuel-price-delta-toast-dismiss"
        >
          <span className="text-lg leading-none">×</span>
        </button>
      </div>
    </div>
  );
};

export default FuelPriceDeltaToast;
